import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Events from './components/Events.jsx';
import TournamentCreator from './components/TournamentCreator.jsx';
import TournamentCreationMenu from './components/TournamentCreationMenu.jsx';
import TournamentDetails from './components/TournamentDetails.jsx';
import TournamentList from './components/TournamentList.jsx';
import TournamentSummary from './components/TournamentSummary.jsx';
import TournamentRouter from './components/TournamentRouter.jsx';
import RankingDetails from './components/RankingDetails.jsx';
import MainLayout from './components/MainLayout.jsx';
import Settings from './components/Settings.jsx';
import Login from './components/Login.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import { ConfirmationProvider } from './contexts/ConfirmationContext.jsx';

import { Snackbar } from './components/Snackbar.jsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './components/TournamentCreationMenu.css';

// Inner app component that uses the auth context
function AppContent() {
  const [snackbar, setSnackbar] = useState({ message: '', isVisible: false });
  const [showCreationMenu, setShowCreationMenu] = useState(false);
  const { isAuthenticated, isLoading, isInitialized } = useAuth();

  useEffect(() => {
    // Make snackbar globally available
    window.showSnackbarGlobal = (message) => {
      setSnackbar({ message, isVisible: true });
    };

    // Add global unhandled promise rejection handler
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      console.error('Promise:', event.promise);
      
      // Prevent the default browser behavior
      event.preventDefault();
      
      // Show user-friendly error message
      setSnackbar({ 
        message: 'Wystąpił nieoczekiwany błąd. Spróbuj odświeżyć stronę.', 
        isVisible: true 
      });
    };

    // Add global error handler
    const handleGlobalError = (event) => {
      console.error('Global error:', event.error);
      console.error('Error message:', event.message);
      console.error('Error source:', event.filename);
      console.error('Error line:', event.lineno);
      
      // Show user-friendly error message
      setSnackbar({ 
        message: 'Wystąpił błąd aplikacji. Spróbuj odświeżyć stronę.', 
        isVisible: true 
      });
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleGlobalError);

    return () => {
      delete window.showSnackbarGlobal;
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleGlobalError);
    };
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar({ message: '', isVisible: false });
  };

  // Make the creation menu globally available
  useEffect(() => {
    window.openTournamentCreationMenu = () => {
      console.log('Opening tournament creation menu');
      setShowCreationMenu(true);
    };
    
    return () => {
      delete window.openTournamentCreationMenu;
    };
  }, []);

  // Show loading screen while auth is initializing
  if (!isInitialized || isLoading) {
    return (
      <div className="App container-fluid">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-11 col-xl-10 px-0 px-md-3 px-lg-4 d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Ładowanie...</span>
              </div>
              <h4 className="text-muted">Inicjalizacja aplikacji...</h4>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <MainLayout>
          <Routes>
            <Route path="/" element={<TournamentList />} />
            <Route path="/events" element={<Events />} />
            <Route path="/ranking" element={<RankingDetails />} />
            <Route path="/ustawienia" element={<Settings />} />
            <Route path="/tournament-creator" element={
              <ProtectedRoute>
                <TournamentCreator />
              </ProtectedRoute>
            } />
            <Route path="/tournament/:id" element={<TournamentRouter />} />
            <Route path="/summary/:id" element={<TournamentSummary />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </MainLayout>
        
        <Snackbar
          message={snackbar.message}
          isVisible={snackbar.isVisible}
          onClose={handleSnackbarClose}
        />
        
        {/* Tournament Creation Menu Modal */}
        <TournamentCreationMenu 
          isOpen={showCreationMenu}
          onClose={() => setShowCreationMenu(false)}
        />
        
        {/* Login Modal - now self-contained */}
        <Login />
      </div>
    </Router>
  );
}

// Main App component that provides the auth and confirmation contexts
function App() {
  return (
    <AuthProvider>
      <ConfirmationProvider>
        <AppContent />
      </ConfirmationProvider>
    </AuthProvider>
  );
}

export default App;
